import axios from 'axios';
import { actions as allActions, AsyncAction } from '..';
import { Asset } from '../../types/asset';
import { assetFactory } from '../../types/factory';
import { uploadPictures } from '../plan/plan';
import { filterValidReferenceDocuments } from '../plan/reference';

type State = {
    fetching: boolean;
    status: 'idle' | 'updating' | 'success' | 'fail';
    asset?: Asset;
    changed?: Asset;
    changes: { date: Date; change: Partial<Asset> }[];
    error?: Error;
};

type Action =
    | { type: 'asset/reset' }
    | { type: 'asset/status'; payload: { status: State['status'] } }
    | { type: 'asset/set'; payload?: { asset: Asset }; error?: Error }
    | { type: 'asset/edit'; payload: { change: Partial<Asset> } }
    | { type: 'asset/commit' }
    | { type: 'asset/discard'; payload: { all: boolean } };

const initialState: State = {
    fetching: false,
    status: 'idle',
    changes: [],
};

/**
 * Apply `changes` to an `asset` object returning a new object.
 *
 * @param asset the base asset
 * @param changes changes to be applied
 * @returns updated object
 */
const withChanges = <T extends Asset | Partial<Asset>>(asset?: T, changes?: Partial<Asset>) =>
    ({ ...asset, ...changes, metadata: { ...asset?.metadata, ...changes?.metadata } } as T);

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case 'asset/reset': {
            return { ...initialState };
        }
        case 'asset/status': {
            return { ...state, ...action.payload };
        }
        case 'asset/set': {
            if (action.payload)
                return { ...state, fetching: false, ...action.payload, changed: action.payload.asset, changes: [] };
            if (action.error) return { ...state, fetching: false, error: action.error };
            return { ...state, fetching: true };
        }
        case 'asset/edit': {
            if (!state.asset) return { ...state, error: Error('cannot edit undefined asset') };
            const changes = [...state.changes, { date: new Date(), ...action.payload }];
            const changed = changes.reduce((acc, c) => withChanges(acc, c.change), state.asset);
            return { ...state, changes, changed };
        }
        case 'asset/commit': {
            if (!state.asset) return { ...state, error: Error('cannot commit undefined asset') };
            return { ...state, status: 'idle', asset: state.changed, changes: [] };
        }
        case 'asset/discard': {
            if (!state.asset) return { ...state, error: Error('cannot discard undefined asset') };
            if (action.payload.all) state.changes = [];
            else state.changes.pop();
            const changed = state.changes.reduce((acc, c) => withChanges(acc, c.change), state.asset);
            return { ...state, changed };
        }
        default:
            return state;
    }
};

/**
 * Reset the reducer to the initial state.
 */
const reset = (): Action => ({ type: 'asset/reset' });

/**
 * Create a new asset locally.
 *
 * @param rethrow throw the internal error
 */
const create = (rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'asset/status', payload: { status: 'idle' } });
        dispatch({ type: 'asset/set' });
        dispatch({ type: 'asset/logs/reset' });
        dispatch({ type: 'asset/set', payload: { asset: assetFactory('#') } });
    } catch (error) {
        dispatch({ type: 'asset/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Get an existing asset from the backend.
 *
 * @param code asset code
 * @param rethrow throw the internal error
 */
const get = (code: string, rethrow = false): AsyncAction => async dispatch => {
    try {
        dispatch({ type: 'asset/status', payload: { status: 'idle' } });
        dispatch({ type: 'asset/set' });
        dispatch({ type: 'asset/logs/reset' });
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'asset/set': cancel } });
        const response = await axios.get<Asset>(`/assets/${code}/`, { cancelToken: cancel.token });
        const asset = response.data;
        if (asset.type) asset.type.reference_documents = filterValidReferenceDocuments(asset.type.reference_documents);
        dispatch({ type: 'asset/set', payload: { asset: response.data } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'asset/set', error });
        if (rethrow) throw error;
    }
};

/**
 * Update the local asset to the backend.
 *
 * @param code asset code
 * @param rethrow throw the internal error
 */
const update = (rethrow = false): AsyncAction => async (dispatch, getState) => {
    const { asset } = getState();
    try {
        dispatch({ type: 'asset/status', payload: { status: 'updating' } });
        dispatch({ type: 'asset/set' });
        if (!asset.asset) throw Error('asset not available in redux store');
        const cancel = axios.CancelToken.source();
        dispatch({ type: 'cancel/set', payload: { 'asset/set': cancel } });
        const action = asset.asset.code.startsWith('#') ? axios.post : axios.put;
        const url = asset.asset.code.startsWith('#') ? '/assets/' : `/assets/${asset.asset.code}/`;
        const picture = (await uploadPictures(asset.asset!.picture !== '' ? [asset.asset.picture] : []))[0] ?? '';
        const response = await action<Asset>(url, { ...asset.asset, picture }, { cancelToken: cancel.token });
        dispatch({ type: 'asset/set', payload: { asset: response.data } });
        dispatch({ type: 'asset/status', payload: { status: 'success' } });
    } catch (error) {
        if (axios.isCancel(error)) return;
        dispatch({ type: 'asset/set', error });
        dispatch({ type: 'asset/status', payload: { status: 'fail' } });
        if (rethrow) throw error;
    }
};

/**
 * Update the asset information locally, but without committing it.
 *
 * @param change asset object changes
 */
const edit = (change: Partial<Asset>): Action => ({ type: 'asset/edit', payload: { change } });

/**
 * Commit the edited asset, the old asset information cannot be restored.
 */
const commit = (): Action => ({ type: 'asset/commit' });

/**
 * Discard the edited asset.
 *
 * @param all if should discard all changes, not only the last
 */
const discard = (all = false): Action => ({ type: 'asset/discard', payload: { all } });

/**
 * Combined commit for assets and plans.
 * This creator prevents missing asset or plan errors, and only remove actions from one of the stores if `!all`.
 */
const sharedCommit = (): AsyncAction => async (dispatch, getState) => {
    const { asset, plan } = getState();
    if (asset.asset) dispatch(commit());
    if (plan.plan) dispatch(allActions.plan.commit());
};

/**
 * Combined discard for assets and plans.
 * This creator prevents missing asset or plan errors, and only remove actions from one of the stores if `!all`.
 *
 * @param all if should discard all changes, not only the last
 */
const sharedDiscard = (all = false): AsyncAction => async (dispatch, getState) => {
    const { asset, plan } = getState();
    if (all) {
        if (asset.asset) dispatch(discard(all));
        if (plan.plan) dispatch(allActions.plan.discard(all));
        return;
    }
    if (asset.asset && plan.plan) {
        const action =
            plan.changes.length === 0
                ? discard
                : asset.changes.length === 0
                ? allActions.plan.discard
                : asset.changes[asset.changes.length - 1].date > plan.changes[plan.changes.length - 1].date
                ? discard
                : allActions.plan.discard;
        dispatch(action(all));
    } else if (asset.asset) dispatch(discard(all));
    else if (plan.plan) dispatch(allActions.plan.discard(all));
};

export const creators = { reset, create, get, update, edit, commit, discard, sharedCommit, sharedDiscard };
